import { ThreadTask } from '@langchain/langgraph-sdk';

import * as React from 'react';

import { ThreadState } from '../api';
import { parseForeignData } from '../data/foreign-data';
import {
  Checkpoint,
  TraceLog,
  Updates,
  getCheckpointId,
  getParentCheckpointId,
  historyToTraceLog,
  isCheckpoint,
  isTaskResult,
} from '../data/misc';
import * as Tree from '../data/tree';

const getNodeMap = (history: ThreadState[]): Record<string, ThreadState> =>
  history.reduce<Record<string, ThreadState>>((acc, x) => {
    acc[getCheckpointId(x)] = x;
    return acc;
  }, {});

const getChildMap = (history: ThreadState[], namespace: string | undefined) => {
  return history.reduce<Record<string, Array<string>>>((acc, x) => {
    const ckey = getCheckpointId(x);
    const ns = x.parent_checkpoint?.checkpoint_ns ?? x.checkpoint.checkpoint_ns;

    const isSameNamespace = namespace ? ns === namespace : !ns;
    if (!isSameNamespace) return acc;

    let pkey = getParentCheckpointId(x) ?? '$';

    if (ckey === pkey) {
      console.error(`Checkpoint ${ckey} is its own parent`);
      pkey = '$';
    }

    acc[pkey] ??= [];
    acc[pkey].unshift(ckey);

    return acc;
  }, {});
};

export const fixHistory = (threadStates: ThreadState[]) => {
  const writesById: Record<string, Record<string, unknown> | null | undefined> =
    {};
  const reparentById: Record<
    string,
    string | ThreadState['parent_checkpoint']
  > = {};

  const lst: ThreadState[] = [];
  for (let i = threadStates.length - 1; i >= 0; i--) {
    const item = { ...threadStates[i] };

    // check if item does not have a checkpoint, but has a checkpoint_id
    // @ts-expect-error checkpoint_id is deprecated
    if (item.checkpoint == null && item.checkpoint_id != null) {
      item.checkpoint = {
        // @ts-expect-error checkpoint_id is deprecated
        checkpoint_id: item.checkpoint_id,
        thread_id: (item.metadata?.thread_id as string | undefined) ?? '',
        checkpoint_map: {},
        checkpoint_ns: '',
      };
    }

    let parentId = getParentCheckpointId(item);
    if (parentId && parentId in reparentById) {
      const newParent = reparentById[parentId];

      if (typeof newParent === 'string') {
        item.parent_checkpoint = {
          checkpoint_id: newParent,
          checkpoint_map: {},
          checkpoint_ns: '',
          thread_id: '',
        };
        // @ts-expect-error parent_checkpoint_id is deprecated
        item.parent_checkpoint_id = newParent;
      } else {
        item.parent_checkpoint = newParent;
        // @ts-expect-error parent_checkpoint_id is deprecated
        item.parent_checkpoint_id = newParent?.checkpoint_id;
      }
    }

    // NOTE: metadata.writes is only Record<string, unknown> when meta.source === "input",
    // otherwise it's a Record<node: string, Record<string, unknown>>
    if (item.metadata?.source === 'input') {
      let write = item.metadata?.writes;
      if (
        write != null &&
        typeof write === 'object' &&
        !('__start__' in write)
      ) {
        write = { __start__: write };
      }

      writesById[getCheckpointId(item)] = write;
      item.metadata = { ...(item.metadata ?? {}), writes: null };

      lst.push(item);
      continue;
    }

    parentId = getParentCheckpointId(item);
    if (
      item.metadata?.source === 'loop' &&
      parentId != null &&
      parentId in writesById
    ) {
      item.metadata = {
        ...(item.metadata ?? {}),
        writes: {
          ...writesById[parentId],
          ...item.metadata?.writes,
        },
      };
      lst.push(item);
      continue;
    }

    // try to detect null nodes created when updating_state(values)
    // this is to properly support forking.
    if (
      item.metadata?.source === 'update' &&
      item.tasks.every((task) => task.result == null) &&
      (item.metadata?.writes == null ||
        (Object.keys(item.metadata?.writes ?? {}).length <= 1 &&
          Object.values(item.metadata?.writes ?? {}).every((o) => o == null)))
    ) {
      reparentById[getCheckpointId(item)] =
        item.parent_checkpoint ?? getParentCheckpointId(item);
      continue;
    }

    lst.push(item);
    continue;
  }

  return lst.reverse();
};

export const mergeHistory = (
  threadStates: ThreadState[] | undefined | null,
  updates: Array<Updates> | undefined
) => {
  const serverHistory = (threadStates ?? []).slice();

  const presentHistory = new Set(
    serverHistory.map((i) => getCheckpointId(i)) ?? []
  );
  const streamHistory: ThreadState[] = (
    (updates?.filter((x) => isCheckpoint(x)) as Checkpoint[]) ?? []
  )
    // filter out updates that have no checkpoint id or are already present in the server history
    .filter((x) => getCheckpointId(x.payload) != null)
    .filter((x) => !presentHistory.has(getCheckpointId(x.payload)))
    .reverse()
    .map((i) => i.payload);

  const checkpointHistory = [...streamHistory, ...serverHistory];

  const taskIdToThreadTask = checkpointHistory.reduce<
    Record<string, ThreadTask>
  >((acc, x) => {
    if (x.tasks == null) return acc;
    for (const task of x.tasks) acc[task.id] = task;
    return acc;
  }, {});

  updates?.forEach((update) => {
    if (isTaskResult(update) && update.payload.id in taskIdToThreadTask) {
      const task = taskIdToThreadTask[update.payload.id];
      if (!task.result && !task.error) {
        if (update.payload.interrupts)
          task.interrupts = update.payload.interrupts;
        if (update.payload.result)
          task.result = Array.isArray(update.payload.result)
            ? Object.fromEntries(update.payload.result)
            : update.payload.result;
        if (update.payload.error) task.error = update.payload.error;
      }
      task.result = update.payload.result;
    }
  });

  return checkpointHistory;
};

export const buildForestFromHistory = (
  history: ThreadState[],
  namespace: string | undefined
): Tree.Forest<ThreadState> => {
  const nodeMap = getNodeMap(history);
  const childMap = getChildMap(history, namespace);

  const buildForest = (
    key: string,
    path: Tree.ForestPath = [],
    forks: Tree.ForestPath[] = []
  ): Tree.Forest<ThreadState> => {
    const children = childMap[key] ?? [];
    const value = nodeMap[key] as ThreadState;

    const nodes: Tree.Forest<ThreadState> = value
      ? [{ type: 'leaf', path, forks, value }]
      : [];

    if (children.length === 0) return nodes;

    if (children.length === 1) {
      const k = children[0];
      return [...nodes, ...buildForest(k, path)];
    }

    const newForks = children.map((_, i) => [...path, i]);
    const newChildren = children.map((k, i) =>
      buildForest(k, newForks[i], newForks)
    );
    return [...nodes, { type: 'fork', children: newChildren }];
  };

  return buildForest('$');
};

// tasks[number].result is incorrect for forked checkpoints, so we need
// to use the metadata.writes instead. We also use metadata.writes for functional API.
export const replaceForkTaskResults = (parts: TraceLog[][]): TraceLog[][] => {
  const mutate: TraceLog[][] = parts.slice();

  for (let i = 1; i < mutate.length; i++) {
    const [checkpoint] = mutate[i];
    const writes = Object.entries(checkpoint?.state?.metadata?.writes ?? {});
    if (writes.length === 0) continue;

    const [prevCheckpoint] = mutate[i - 1];
    let prevEntries = mutate[i - 1].slice(1);

    // If it's a checkpoint that causes a fork in history, we need to use the metadata writes
    // for the "delta" preceding the checkpoint, otherwise the original delta will be shown instead.
    const replaceWithWrites =
      checkpoint?.state?.metadata?.source === 'update' ||
      checkpoint?.state?.metadata?.source === 'fork' ||
      // This is done for loop checkpoints, which are created by forking inside a subgraph
      (checkpoint.meta.forks.length > 0 && !!checkpoint.meta.path.at(-1)) ||
      // This happens when task.results is missing but we already have those writes
      prevEntries.every((i) => i.entry.type === 'pending');

    // when user changes the name of nodes, the task will disappear.
    // let's try to mock these tasks back.
    const presentChannels = new Set(prevEntries.map((i) => i.entry.name));
    const extendWithWrites = writes.filter(
      ([name]) => !presentChannels.has(name)
    );

    if (replaceWithWrites) {
      // If we fork from a subgraph checkpoint, we still need to inherit the task
      // from the previous checkpoints to not lose the reference to checkpoint namespace.
      const prevTasks = prevEntries.map((i) => i.task).filter((i) => i != null);
      const inheritTask =
        checkpoint.state?.metadata?.source === 'loop' && prevTasks.length === 1
          ? prevTasks.at(0)
          : undefined;

      prevEntries = writes.map(([name, write]) => {
        const entry = {
          type: 'node' as const,
          id: `${checkpoint.meta.id}-${name}`,
          name,
          data: parseForeignData(write),
          readonly: !presentChannels.has(name),
        };

        return { ...prevCheckpoint, task: inheritTask, entry };
      });
    } else if (extendWithWrites.length > 0) {
      prevEntries = extendWithWrites
        .map(([name, write]): TraceLog => {
          const entry = {
            type: 'node' as const,
            id: `${checkpoint.meta.id}-missing-${name}`,
            name,
            data: parseForeignData(write),
            readonly: true,
          };

          return { ...prevCheckpoint, task: undefined, entry };
        })
        .concat(prevEntries);
    }

    // inherit the previous checkpoint metadata
    // in order to allow editing the same write
    mutate[i - 1] = [prevCheckpoint, ...prevEntries];
  }

  return mutate;
};

const getRunIdsForTraceLog = (logs: TraceLog[]) => {
  const result: Record<string, string> = {};
  const seen = new Set<string>();
  for (let i = logs.length - 1; i >= 0; i -= 1) {
    const log = logs[i];

    if (log.meta.runId && !seen.has(log.meta.runId)) {
      result[log.meta.id] = log.meta.runId;
      seen.add(log.meta.runId);
    }
  }

  return result;
};

export const useTraceLog = (
  threadStates: ThreadState[] | undefined | null,
  state: {
    updates?: Array<Updates>;
    forestPath?: Tree.ForestPath;
    namespace?: string;
  }
) => {
  const history = React.useMemo(() => {
    return mergeHistory(threadStates, state?.updates);
  }, [threadStates, state?.updates]);

  return React.useMemo(() => {
    const flatForest = Tree.flattenAtPath(
      buildForestFromHistory(fixHistory(history), state?.namespace),
      state?.forestPath ?? []
    );

    const traceLogUnflattened = replaceForkTaskResults(
      flatForest.map(historyToTraceLog)
    );

    const traceLog = traceLogUnflattened.flat();

    const threadHead = flatForest.at(-1)?.value;
    const runIdsMap = getRunIdsForTraceLog(traceLog);

    return { traceLog, threadHead, runIdsMap };
  }, [history, state?.forestPath, state?.namespace]);
};
